import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n/useTranslation";
import SiteFooter from "../components/SiteFooter";
import { API_BASE_URL } from "../config/apiBaseUrl";
import { DIGITAL_BOOK_INVITE_STORAGE_KEY } from "../constants/digitalBookInvite";

const AUTH_TOKEN_KEY = "mysipur_auth_token";

const Login: React.FC = () => {
  const { login, user, token, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const q = searchParams.get("digitalBookInvite")?.trim();
    if (q) {
      sessionStorage.setItem(DIGITAL_BOOK_INVITE_STORAGE_KEY, q);
    }
  }, [searchParams]);

  const resolveDigitalBookInviteAndNavigate = useCallback(async () => {
    const invite = sessionStorage.getItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
    if (!invite) return false;
    const authToken = token || sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (!authToken) return false;
    const res = await fetch(`${API_BASE_URL}/stories/digital-book-invite/resolve`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token: invite }),
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok || !payload?.data?.sessionId) {
      sessionStorage.removeItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
      throw new Error(payload?.message || t("auth.login.errors.digitalBookInviteFailed"));
    }
    sessionStorage.removeItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
    navigate(
      `/create/digital-book?sessionId=${encodeURIComponent(payload.data.sessionId)}`,
      { replace: true }
    );
    return true;
  }, [navigate, t, token]);

  useEffect(() => {
    if (authLoading) return;
    const invite = sessionStorage.getItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
    if (!invite || !user || !token) return;
    if (user.isAdmin === true) {
      sessionStorage.removeItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await resolveDigitalBookInviteAndNavigate();
      } catch {
        if (!cancelled) {
          setError(t("auth.login.errors.digitalBookInviteFailed"));
          navigate("/create/order-summary", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, token, resolveDigitalBookInviteAndNavigate, navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!email.trim()) throw new Error(t("auth.login.errors.emailRequired"));
      if (!password) throw new Error(t("auth.login.errors.passwordRequired"));

      const result = await login(email.trim(), password);
      if (result.needsAdminVerification) {
        navigate("/admin-verification");
        return;
      }
      if (result.isAdmin) {
        sessionStorage.removeItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
        navigate("/admin/submissions");
        return;
      }
      const invite = sessionStorage.getItem(DIGITAL_BOOK_INVITE_STORAGE_KEY);
      if (invite) {
        try {
          const ok = await resolveDigitalBookInviteAndNavigate();
          if (ok) return;
        } catch (inviteErr: unknown) {
          const msg = inviteErr instanceof Error ? inviteErr.message : String(inviteErr);
          setError(msg || t("auth.login.errors.digitalBookInviteFailed"));
          navigate("/create/language", { replace: true });
          return;
        }
      }
      navigate("/create/language");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || t("auth.login.errors.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/", { replace: true });
  };

  const errorId = "login-form-error";

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/home/sign-in.png')" }}
      />
      <div className="absolute inset-0 bg-brandBlue/20" />

      <div className="relative z-10 flex w-full flex-1 items-center justify-center p-4">
        <main
          id="main-content"
          tabIndex={-1}
          className="flex w-full max-w-md flex-col items-center outline-none"
        >
          <form
            onSubmit={handleSubmit}
            className="w-full bg-brandBeige/40 backdrop-blur-md rounded-2xl shadow-card border border-white/30 p-6"
            aria-describedby={error ? errorId : undefined}
          >
            <h1 className="text-2xl font-extrabold text-heading mb-2">{t("auth.login.title")}</h1>
            <p className="text-body mb-5">{t("auth.login.subtitle")}</p>

            {error && (
              <div
                id={errorId}
                role="alert"
                aria-live="polite"
                className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg shadow-soft mb-4"
              >
                <span className="sr-only">{t("auth.login.errorAnnouncement")}: </span>
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-heading mb-1" htmlFor="login-email">
                  {t("auth.login.email")}
                  <span className="text-red-700" aria-hidden="true">
                    {" "}
                    *
                  </span>
                  <span className="sr-only"> {t("a11y.requiredSr")}</span>
                </label>
                <input
                  id="login-email"
                  dir="ltr"
                  className="w-full p-2 border border-gray-300 rounded-md text-left"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="email"
                  required
                  aria-invalid={error ? true : undefined}
                  aria-required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-heading mb-1" htmlFor="login-password">
                  {t("auth.login.password")}
                  <span className="text-red-700" aria-hidden="true">
                    {" "}
                    *
                  </span>
                  <span className="sr-only"> {t("a11y.requiredSr")}</span>
                </label>
                <input
                  id="login-password"
                  dir="ltr"
                  className="w-full p-2 border border-gray-300 rounded-md text-left"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                  required
                  aria-invalid={error ? true : undefined}
                  aria-required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !email.trim() || !password}
                className="w-full bg-brandOrange hover:bg-orange-600 text-white font-semibold py-3 rounded-2xl shadow-card disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
              >
                {isLoading ? t("auth.login.submitting") : t("auth.login.submit")}
              </button>
            </div>

            <p className="text-sm text-gray-600 mt-5">
              {t("auth.login.noAccount")}{" "}
              <Link to="/signup" className="text-brandNavy font-semibold hover:underline">
                {t("auth.login.signUp")}
              </Link>
            </p>
          </form>

          <div className="mt-4 w-full flex justify-center">
            <button
              type="button"
              onClick={handleBack}
              disabled={isLoading}
              className="bg-brandNavy/90 hover:bg-brandNavy text-white font-semibold py-3 px-10 rounded-2xl shadow-soft transition-all duration-200 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("wizard.common.back")}
            </button>
          </div>
        </main>
      </div>
      <div className="relative z-10 mx-auto w-full max-w-md px-4 pb-4">
        <SiteFooter />
      </div>
    </div>
  );
};

export default Login;
